import { z } from 'zod';

import { colorChoiceSchema, optionalPxSchema } from '@/features/storefront/colorChoice';
import {
  LANGUAGE_DISPLAYS,
  MARQUEE_SPEEDS,
  focalPointSchema,
  linkDirectoryBlockSchema,
  marqueeItemBlockSchema,
  newElementId,
  type MarqueeItemBlock,
} from '@/features/storefront/sections/schema';
import { SOCIAL_PLATFORMS } from '@/features/storefront/sections/social';

/**
 * The one definition of a theme document, mirroring `ThemeConfig` in
 * `app/schemas/store.py`.
 *
 * Both sides of the app read themes - the dashboard edits them, the storefront
 * wears them - and each used to carry its own copy of this schema, which is a
 * drift waiting to be published. This module is the single source, the same
 * treatment `sections/schema.ts` already gives sections.
 *
 * Every field added after launch is defaulted, so a document saved before the
 * field existed parses to exactly what it rendered then.
 */

export const themeColorsSchema = z.object({
  primary: z.string(),
  background: z.string(),
  surface: z.string(),
  text: z.string(),
  muted: z.string(),
});

export {
  PALETTE_COLOR_KEYS,
  colorChoiceSchema,
  isPaletteColorKey,
  type ColorChoice,
  type PaletteColorKey,
} from '@/features/storefront/colorChoice';

export {
  SECTION_ORIGINS,
  THEME_SAMPLE_ORIGIN,
  type SectionOrigin,
} from '@/features/storefront/sections/schema';

const optionalPx = optionalPxSchema;

export const themeTypographySchema = z.object({
  font_family: z.string(),
  // null means "same as font_family". Editorial themes put a serif here.
  heading_font: z
    .string()
    .nullish()
    .transform((value) => value ?? null),
  heading_weight: z.enum(['500', '600', '700', '800']),
  base_size_px: z.number().default(16),
});

export const themeButtonsSchema = z.object({
  radius_px: z.number(),
  style: z.enum(['solid', 'outline']),
});

/**
 * One message on the announcement rail above the masthead.
 *
 * Mirrors `HeaderBanner` in `app/schemas/store.py`. Optional copy arrives as
 * `null` from the API and as `''` from a cleared field, so both land on `''`.
 */
export const headerBannerSchema = z.object({
  id: z.string().default(newElementId),
  text: z.string(),
  href: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  link_label: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
});

/** How a department in the header's category navigation opens. Mirrors `MegaMenuStyle`. */
export const MEGA_MENU_STYLES = ['off', 'columns', 'columns_with_promo', 'columns_with_cards'] as const;
export type MegaMenuStyle = (typeof MEGA_MENU_STYLES)[number];

/** One picture card beside a mega panel's columns. Mirrors `MegaMenuCard`. */
export const megaMenuCardSchema = z.object({
  heading: z.string().catch('').default(''),
  image_url: z.string().nullish().catch(null),
  href: z.string().nullish().catch(null),
});
export type MegaMenuCard = z.infer<typeof megaMenuCardSchema>;

/**
 * The mastheads a shop may wear: its theme's own, or the shared one with the
 * logo centred between the menu and the shopper's icons. Mirrors
 * `ThemeHeader.layout`.
 */
export const HEADER_LAYOUTS = ['theme', 'centered'] as const;
export type HeaderLayout = (typeof HEADER_LAYOUTS)[number];

export const themeHeaderSchema = z.object({
  // Optional, like the colours below: absent is the theme's own masthead, and
  // an unknown value reads as it too.
  layout: z.enum(HEADER_LAYOUTS).catch('theme').optional(),
  logo_width_px: z.number().default(120),
  show_store_name: z.boolean().default(true),
  sticky: z.boolean().default(true),
  // Defaulted like everything added after launch: a theme saved before the
  // rail existed parses to a header with no rail, which is what it rendered.
  banners: z.array(headerBannerSchema).default([]),
  banner_autoplay: z.boolean().default(true),
  banner_interval_seconds: z.number().default(5),
  // The department-panel opener's own words, for themes with one (currently
  // only Tools & Machine). Defaulted to what every such theme already renders.
  department_menu_label: z.string().default('All departments'),
  department_bar_tagline: z.string().default('Trade counter · Mon-Sat 8-6'),
  // How many departments the masthead's category strip stands in, for the
  // themes whose header navigates by them. 0 is "the number this theme was
  // drawn with" - the default, and what a theme saved before merchants could
  // choose parses to, so nothing moves until one of them sets a number.
  category_menu_limit: z.number().default(0),
  // A "Deliver to <pincode>" control between the logo and the search, and
  // small round pictures before the header's department links. Both off by
  // default, so a theme saved before either existed renders as it did.
  location_selector: z.boolean().default(false),
  category_icons: z.boolean().default(false),
  // Whether a department in the category navigation opens a full-width panel
  // of columns (and a promo tile). Off by default - what every theme saved
  // before it existed renders - and an unknown value reads as off. Optional,
  // like the colours below, so a header built by hand is still a header.
  mega_menu: z.enum(MEGA_MENU_STYLES).catch('off').optional(),
  mega_menu_promo_heading: z.string().catch('').optional(),
  mega_menu_promo_image_url: z.string().nullish().catch(null),
  mega_menu_promo_href: z.string().nullish().catch(null),
  // The two cards of `columns_with_cards`; fewer leaves the rest to defaults.
  mega_menu_cards: z.array(megaMenuCardSchema).max(2).catch([]).optional(),
  // The masthead's own colours and the line under it. Optional rather than
  // defaulted, like everything below: unset is the theme's own look, and a
  // header built by hand in a test is still a header.
  background: colorChoiceSchema,
  text_color: colorChoiceSchema,
  divider_px: optionalPx,
  divider_color: colorChoiceSchema,
  // The announcement rail's, likewise.
  announcement_background: colorChoiceSchema,
  announcement_text_color: colorChoiceSchema,
  announcement_width: z.enum(['full', 'page']).catch('full').optional(),
  announcement_padding_top_px: optionalPx,
  announcement_padding_bottom_px: optionalPx,
  announcement_divider_px: optionalPx,
  announcement_divider_color: colorChoiceSchema,
  // The masthead's own primary button ("Book appointment"), after the
  // shopper's corner in every header layout. Both unset by default, and drawn
  // only with a label and a link, so a header saved before it existed renders
  // as it did. Mirrors `ThemeHeader.cta_label` / `cta_href`.
  cta_label: z.string().max(30).nullish().catch(null),
  cta_href: z.string().nullish().catch(null),
});

/** The footers a shop may wear: its theme's own, the shared columns, or the large one. */
export const FOOTER_LAYOUTS = ['theme', 'columns', 'large'] as const;
export type FooterLayout = (typeof FOOTER_LAYOUTS)[number];

/** The most social links the columns footer carries. Mirrors `ThemeFooter.social`. */
export const FOOTER_SOCIAL_MAX = 8;

/** The most items the benefits strip above the footer carries. Mirrors `ThemeFooter.ribbon_items`. */
export const FOOTER_RIBBON_MAX = 12;

/** How dark the veil over the large footer's photo is when unset. Mirrors `ThemeFooter.background_overlay`. */
export const FOOTER_OVERLAY_DEFAULT = 60;

/**
 * One profile the columns footer links to. Mirrors `FooterSocialLink` in
 * `app/schemas/store.py`: the networks a `social_links` section offers, and
 * the url as the merchant typed it (resolved through `socialLinkHref`).
 */
export const footerSocialLinkSchema = z.object({
  network: z.enum(SOCIAL_PLATFORMS).catch('custom'),
  url: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
});

export type FooterSocialLink = z.infer<typeof footerSocialLinkSchema>;

/** Where the footer's link directory comes from. Mirrors `ThemeFooter.directory`. */
export const FOOTER_DIRECTORY_SOURCES = ['off', 'categories', 'manual'] as const;
export type FooterDirectorySource = (typeof FOOTER_DIRECTORY_SOURCES)[number];

/** The most groups the footer's directory carries. Mirrors `ThemeFooter.directory_blocks`. */
export const FOOTER_DIRECTORY_MAX = 24;

/**
 * The footer's appearance. Mirrors `ThemeFooter` in `app/schemas/store.py`;
 * every field unset is the theme's own footer, exactly as it rendered before.
 */
export const themeFooterSchema = z.object({
  // `theme` is the family's own footer; `columns` the shared three-column one
  // any family can wear (see `layouts/chrome/ColumnsFooter.tsx`). Optional,
  // like everything here: absent is the theme's own.
  layout: z.enum(FOOTER_LAYOUTS).catch('theme').optional(),
  // Whether the columns footer shows `social` under the store's name.
  show_social: z.boolean().nullish().optional(),
  // The shop's profiles, for the columns footer. A row this build cannot read
  // is dropped rather than failing the footer.
  social: z
    .array(z.unknown())
    .transform((rows) =>
      rows
        .flatMap((row) => {
          const parsed = footerSocialLinkSchema.safeParse(row);
          return parsed.success ? [parsed.data] : [];
        })
        .slice(0, FOOTER_SOCIAL_MAX),
    )
    .optional(),
  background: colorChoiceSchema,
  text_color: colorChoiceSchema,
  // Room added above and below the theme's own footer.
  padding_top_px: optionalPx,
  padding_bottom_px: optionalPx,
  copyright_size_px: optionalPx,
  copyright_uppercase: z.boolean().nullish().optional(),
  // The large footer's photo, the veil over it (0-80, 60 unset) and its
  // bottom bar's colours.
  background_image_url: z.string().nullish().catch(null),
  background_image_focal: focalPointSchema.nullish().catch(null),
  background_overlay: z.number().min(0).max(80).nullish().catch(null),
  bottom_bar_background: colorChoiceSchema,
  bottom_bar_text_color: colorChoiceSchema,
  // The benefits strip above whichever footer is worn. Off unless switched on;
  // a row this build cannot read is dropped rather than failing the footer.
  ribbon_enabled: z.boolean().nullish().optional(),
  ribbon_items: z
    .array(z.unknown())
    .transform((rows) =>
      rows
        .flatMap((row) => {
          const parsed = marqueeItemBlockSchema.safeParse(row);
          return parsed.success ? [parsed.data] : [];
        })
        .slice(0, FOOTER_RIBBON_MAX),
    )
    .optional(),
  ribbon_speed: z.enum(MARQUEE_SPEEDS).catch('normal').optional(),
  ribbon_background: colorChoiceSchema,
  ribbon_text_color: colorChoiceSchema,
  // The large footer's look; `framed` is the ribbon inside it, bold column
  // headings, the logo and a signup last, and an inset bottom bar.
  large_style: z.enum(['standard', 'framed', 'contact']).catch('standard').optional(),
  newsletter_heading: z.string().catch('').optional(),
  newsletter_text: z.string().catch('').optional(),
  // The dense link directory above whichever footer is worn: off, the shop's
  // own categories, or the merchant's groups (`directory_blocks`). A group
  // this build cannot read is dropped rather than failing the footer.
  directory: z.enum(FOOTER_DIRECTORY_SOURCES).catch('off').optional(),
  directory_title: z.string().nullish().catch(null),
  directory_blocks: z
    .array(z.unknown())
    .transform((rows) =>
      rows
        .flatMap((row) => {
          const parsed = linkDirectoryBlockSchema.safeParse(row);
          return parsed.success ? [parsed.data] : [];
        })
        .slice(0, FOOTER_DIRECTORY_MAX),
    )
    .optional(),
  // The merchant's own app listings: a store badge in the footer for each.
  // A link that is not the official store is dropped, never drawn.
  play_store_url: z.string().nullish().catch(null),
  app_store_url: z.string().nullish().catch(null),
});

/** The host each app badge may point at. Mirrors `PlayStoreUrl` / `AppStoreUrl`. */
export const APP_STORE_HOSTS = {
  play_store_url: 'play.google.com',
  app_store_url: 'apps.apple.com',
} as const;

/**
 * An app listing the footer may link to: `https://` on that store's own host,
 * or null. The server refuses anything else; this keeps an older document, or
 * a value mid-typing in the editor, from ever becoming a link.
 */
export function appStoreHref(
  field: keyof typeof APP_STORE_HOSTS,
  value: string | null | undefined,
): string | null {
  const trimmed = (value ?? '').trim();
  if (!trimmed || /\s/.test(trimmed)) return null;
  try {
    const url = new URL(trimmed);
    if (url.protocol !== 'https:' || url.hostname.toLowerCase() !== APP_STORE_HOSTS[field]) return null;
    if (url.username || url.password || (url.port && url.port !== '443')) return null;
    return trimmed;
  } catch {
    return null;
  }
}

/** A blank ribbon row, for the dashboard's "add item" button. */
export function newFooterRibbonItem(): MarqueeItemBlock {
  return { id: newElementId(), visible: true, text: '', icon: null };
}

/** A blank rail message, for the dashboard's "add banner" button. */
export function newHeaderBanner(): HeaderBanner {
  return { id: newElementId(), text: '', href: '', link_label: '' };
}

/**
 * The structural looks a storefront can wear.
 *
 * A theme id, not an enum. The themes this build ships are the folders under
 * `previewThemes/`, discovered at build time, and writing that list into a
 * closed union here would mean every new theme edits this file, the backend's
 * matching `Literal`, and the tests that pin both - which is exactly the tax
 * that made adding a theme expensive.
 *
 * The looseness is safe in the only direction that matters: the registry falls
 * back to the base theme for an id it does not have, so a document naming a
 * withdrawn theme renders the shop rather than refusing to parse. The closed
 * list still exists - the backend validates writes against the themes it knows
 * - it just is not duplicated into the shopper's parser.
 */
export const layoutFamilySchema = z.string().min(1).default('classic');

/** `'family'` follows the theme the document names; anything else overrides one area. */
export const layoutAreaSchema = z.string().min(1).default('family');

export const themeLayoutSchema = z
  .object({
    family: layoutFamilySchema,
    header: layoutAreaSchema,
    footer: layoutAreaSchema,
    product: layoutAreaSchema,
    listing: layoutAreaSchema,
    /**
     * The platform catalog theme this document was dressed in, when that is a
     * custom theme drawn in `family`'s layout. Absent or null means `family`
     * itself - which is every built-in. The effective key is
     * `theme_key ?? family` (see `features/themes/availableThemes.ts`).
     */
    theme_key: z.string().nullish(),
  })
  // A theme saved before layouts existed is the classic layout by definition.
  .default({});

/** Where the language switcher is drawn (docs/i18n.md §7). */
export const LANGUAGE_SWITCHER_PLACEMENTS = ['header', 'footer', 'mobile'] as const;

export type LanguageSwitcherPlacement = (typeof LANGUAGE_SWITCHER_PLACEMENTS)[number];

/**
 * The theme's language switcher. Rendered only on a shop with more than one
 * language and with the store's `switcher_enabled` on; `display` is how each
 * language is named (see `LANGUAGE_DISPLAYS` in `sections/schema.ts`).
 */
export const themeLanguageSwitcherSchema = z.object({
  show: z.boolean().default(true),
  placements: z
    .array(z.string())
    // Unknown placements from a newer build are dropped, not a parse error.
    .transform((values) =>
      values.filter((value): value is LanguageSwitcherPlacement =>
        (LANGUAGE_SWITCHER_PLACEMENTS as readonly string[]).includes(value),
      ),
    )
    .default(['header', 'mobile']),
  display: z.enum(LANGUAGE_DISPLAYS).catch('native').default('native'),
});

export type ThemeLanguageSwitcher = z.infer<typeof themeLanguageSwitcherSchema>;

/**
 * The quick cart plugin. `enabled` is three-way: null follows the theme (on
 * for the themes designed around the drawer), true or false is the
 * merchant's own choice. Mirrors `QuickCartPlugin` in `backend/app/schemas/store.py`.
 */
export const quickCartPluginSchema = z.object({
  enabled: z.boolean().nullable().default(null),
  open_on_add: z.boolean().default(true),
  free_shipping_bar: z.boolean().default(true),
  recommendations: z.boolean().default(true),
});

/** The kinds of page a promo popup may open on. Checkout is never one of them. */
export const PROMO_POPUP_PAGES = ['all', 'home', 'product', 'collection', 'cart'] as const;
export type PromoPopupPage = (typeof PROMO_POPUP_PAGES)[number];

/**
 * The promo popup plugin: an offer and an email signup a few seconds after
 * landing. Off until the merchant switches it on. Mirrors `PromoPopupPlugin`
 * in `backend/app/schemas/store.py`; a bad value reads as the default rather
 * than failing the theme.
 */
export const promoPopupPluginSchema = z.object({
  enabled: z.boolean().nullable().default(null),
  show_on: z
    .array(z.string())
    // Unknown pages from a newer build are dropped, not a parse error.
    .transform((values) =>
      values.filter((value): value is PromoPopupPage => (PROMO_POPUP_PAGES as readonly string[]).includes(value)),
    )
    .default(['home']),
  delay_seconds: z.number().int().min(0).max(60).catch(5).default(5),
  eyebrow: z.string().catch('Limited time offer').default('Limited time offer'),
  headline: z.string().catch('The biggest deals, up to 50% off!').default('The biggest deals, up to 50% off!'),
  subtitle: z.string().catch('Shop now and save big today.').default('Shop now and save big today.'),
  button_label: z.string().catch('Submit').default('Submit'),
  image_url: z.string().nullish().transform((value) => value || null),
  panel_color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .nullish()
    .catch(null)
    .transform((value) => value ?? null),
  collect_name: z.boolean().default(true),
});

/** Where the search suggestions open. Mirrors `SearchSuggestStyle`. */
export const SEARCH_SUGGEST_STYLES = ['overlay', 'dropdown'] as const;
export type SearchSuggestStyle = (typeof SEARCH_SUGGEST_STYLES)[number];

/**
 * The search-as-you-type plugin: the header's search icon opens a box with
 * live product and category results. Off until the merchant switches it on.
 * Mirrors `SearchSuggestPlugin` in `backend/app/schemas/store.py`.
 */
export const searchSuggestPluginSchema = z.object({
  enabled: z.boolean().nullable().default(null),
  style: z.enum(SEARCH_SUGGEST_STYLES).catch('overlay').default('overlay'),
  show_products: z.boolean().default(true),
  show_categories: z.boolean().default(true),
  max_results: z.number().int().min(3).max(10).catch(6).default(6),
});

/**
 * The wishlist plugin: hearts on products, a header count and a /wishlist
 * page. Off until the merchant switches it on. Mirrors `WishlistPlugin` in
 * `backend/app/schemas/store.py`.
 */
export const wishlistPluginSchema = z.object({
  enabled: z.boolean().nullable().default(null),
  show_in_header: z.boolean().catch(true).default(true),
  show_on_cards: z.boolean().catch(true).default(true),
  show_on_product: z.boolean().catch(true).default(true),
});

/** Which colours a shopper first sees. Mirrors `ColorModeDefault`. */
export const COLOR_MODE_DEFAULTS = ['theme', 'system'] as const;
export type ColorModeDefault = (typeof COLOR_MODE_DEFAULTS)[number];

const alternateColor = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/)
  .nullish()
  .catch(null)
  .transform((value) => value ?? null);

/**
 * The light and dark mode plugin: the shop in its own colours or in their
 * opposite, with a toggle for the shopper. Off until the merchant switches it
 * on. Each `alternate_*` null is derived from the theme's own colour (see
 * `colorMode.ts`). Mirrors `ColorModePlugin` in `backend/app/schemas/store.py`.
 */
export const colorModePluginSchema = z.object({
  enabled: z.boolean().nullable().default(null),
  default_mode: z.enum(COLOR_MODE_DEFAULTS).catch('theme').default('theme'),
  show_toggle: z.boolean().catch(true).default(true),
  alternate_primary: alternateColor,
  alternate_background: alternateColor,
  alternate_surface: alternateColor,
  alternate_text: alternateColor,
  alternate_muted: alternateColor,
});

/**
 * The app bar plugin: on a phone, the shop's way around - home, categories,
 * search, cart, account - in a bar fixed to the bottom of the screen, the way
 * an installed app has it. Off until the merchant switches it on. Mirrors
 * `AppTabBarPlugin` in `backend/app/schemas/store.py`.
 */
export const appTabBarPluginSchema = z.object({
  enabled: z.boolean().nullable().default(null),
  show_labels: z.boolean().catch(true).default(true),
});

/**
 * Shop-wide add-ons a merchant can switch on whatever theme they wear. See
 * `plugins/registry.ts` for what each one is and how its theme default reads.
 * Defaulted, so every theme saved before plugins existed follows its theme.
 */
export const themePluginsSchema = z.object({
  quick_cart: quickCartPluginSchema.default({}),
  promo_popup: promoPopupPluginSchema.default({}),
  search_suggest: searchSuggestPluginSchema.default({}),
  wishlist: wishlistPluginSchema.default({}),
  color_mode: colorModePluginSchema.default({}),
  app_tab_bar: appTabBarPluginSchema.default({}),
});

export type QuickCartPlugin = z.infer<typeof quickCartPluginSchema>;
export type PromoPopupPlugin = z.infer<typeof promoPopupPluginSchema>;
export type SearchSuggestPlugin = z.infer<typeof searchSuggestPluginSchema>;
export type WishlistPlugin = z.infer<typeof wishlistPluginSchema>;
export type ColorModePlugin = z.infer<typeof colorModePluginSchema>;
export type AppTabBarPlugin = z.infer<typeof appTabBarPluginSchema>;
export type ThemePlugins = z.infer<typeof themePluginsSchema>;

/** How a product card's badges are drawn. Mirrors `CardBadgeStyle`. */
export const CARD_BADGE_STYLES = ['pill', 'ribbon', 'corner'] as const;
export type CardBadgeStyle = (typeof CARD_BADGE_STYLES)[number];
/** The card's own "add to cart". Mirrors `CardQuickAdd`. */
export const CARD_QUICK_ADD = ['off', 'button', 'icon'] as const;
export type CardQuickAdd = (typeof CARD_QUICK_ADD)[number];

/**
 * How every product card in the shop behaves, whatever its presentation.
 * Mirrors `ThemeCards` in `backend/app/schemas/store.py`. Every default is
 * off, so a theme saved before this existed renders the cards it always did;
 * a bad value reads as the default rather than failing the theme. Read it
 * through `themeCards()` to get the defaults.
 */
export const themeCardsSchema = z.object({
  hover_image: z.boolean().catch(false).default(false),
  badges: z
    .object({
      sale: z.boolean().catch(false).default(false),
      new: z.boolean().catch(false).default(false),
      new_days: z.number().int().min(7).max(90).catch(30).default(30),
      bestseller: z.boolean().catch(false).default(false),
      sold_out: z.boolean().catch(false).default(false),
    })
    .catch({ sale: false, new: false, new_days: 30, bestseller: false, sold_out: false })
    .default({}),
  badge_style: z.enum(CARD_BADGE_STYLES).catch('pill').default('pill'),
  swatches: z.boolean().catch(false).default(false),
  quick_add: z.enum(CARD_QUICK_ADD).catch('off').default('off'),
});

export type ThemeCards = z.infer<typeof themeCardsSchema>;

/** A theme's card settings with every default filled in. */
export function themeCards(theme: { cards?: unknown } | null | undefined): ThemeCards {
  const parsed = themeCardsSchema.safeParse(theme?.cards ?? {});
  return parsed.success ? parsed.data : themeCardsSchema.parse({});
}

/** One override field: a bad value is "the family's own", never a parse error. */
const tokenField = <T extends z.ZodTypeAny>(schema: T) =>
  schema.nullish().catch(null).transform((value) => value ?? null);

/**
 * Design constants laid over the layout family's own `meta.ts` tokens.
 * Mirrors `ThemeTokensOverride` in `app/schemas/store.py`.
 *
 * Written by themes built from screenshots on the platform panel: they reuse a
 * built-in family's components but not its accent, spacing or heading voice.
 * Every field `null` is the family's own, so an empty override - and a
 * document with no `tokens` at all, which is every document saved before this
 * existed - renders the family exactly. Unknown keys are dropped; a value this
 * build cannot read falls back to the family rather than failing the theme.
 * `effectiveTokens` in `features/storefront/tokens.ts` is the one place it is
 * laid over the family.
 */
export const themeTokensOverrideSchema = z.object({
  accent: tokenField(z.string().regex(/^#[0-9a-fA-F]{6}$/)),
  mono_font: tokenField(z.string().max(80)),
  display_font: tokenField(z.string().max(80)),
  heading_tracking: tokenField(z.string().regex(/^(normal|-?0?\.\d{1,3}em)$/)),
  heading_transform: tokenField(z.enum(['none', 'uppercase'])),
  heading_align: tokenField(z.enum(['left', 'center'])),
  hero_edge: tokenField(z.enum(['straight', 'slant'])),
  card_ratings: tokenField(z.boolean()),
  band_frame: tokenField(z.enum(['full', 'card'])),
  container_max: tokenField(z.string().regex(/^\d{2,3}(\.\d)?rem$/)),
  space_scale: tokenField(z.number().min(0.5).max(2)),
  header_height: tokenField(z.string().regex(/^\d{1,2}(\.\d{1,2})?rem$/)),
});

export type ThemeTokensOverride = z.infer<typeof themeTokensOverrideSchema>;

export const themeSchema = z.object({
  preset: z.string(),
  colors: themeColorsSchema,
  typography: themeTypographySchema,
  buttons: themeButtonsSchema,
  // Defaulted, so a theme saved before the header had settings still loads.
  header: themeHeaderSchema.default({}),
  // Optional for the reason `language_switcher` is: every hand-built theme
  // object stays a theme, and absent is the theme's own footer.
  footer: themeFooterSchema.optional(),
  layout: themeLayoutSchema,
  // Left loose on purpose. Sections are read through `sections/schema.ts`,
  // which is the one definition of what a section is - and which knows how to
  // skip one it cannot render rather than failing the whole theme.
  //
  // This field is the *home page*, and stays that for as long as documents
  // saved before `pages` existed do. `sections/pageDocument.ts` is what reads
  // either shape; nothing else should reach for this directly.
  //
  // Loose also means every key a section carries survives a round trip -
  // `origin` (see `SECTION_ORIGINS`) most of all, which the typed section
  // schema declares for the same reason.
  sections: z.array(z.record(z.unknown())),
  /**
   * Every customisable page, by id, each a stack of sections in the same shape
   * `sections` holds.
   *
   * Defaulted and loose for the same two reasons the home page's list is: a
   * theme saved before a page was customisable parses to that page's defaults
   * rather than to a parse error, and a page id this build has retired is
   * carried through a save rather than dropped out from under whoever still
   * ships it.
   */
  pages: z.record(z.string(), z.array(z.record(z.unknown()))).default({}),
  /**
   * A theme's own design for the merchant pages a service business needs, by
   * page handle (`CONTENT_PAGE_HANDLES`): each a stack of home-type sections.
   * Mirrors `ThemeConfig.content_pages` in `app/schemas/store.py`.
   *
   * Not pages of the theme - `/pages/about` is the merchant's own page - but
   * what that address shows until they have one: `StorefrontPageView` draws a
   * handle's stack when the shop has no published page there, and applying the
   * theme hands each one to the merchant as a draft page.
   *
   * Optional rather than defaulted, so every theme document saved before
   * these existed round-trips byte for byte; loose for the reason `pages` is.
   */
  content_pages: z.record(z.string(), z.array(z.record(z.unknown()))).optional(),
  /**
   * The merchant's own words for the static copy their theme renders, by
   * content key. See `content/document.ts` for what may be in here and
   * `content/types.ts` for what the keys mean.
   *
   * Loose in the same way `pages` is, and for the stronger version of the same
   * reason: a key belongs to a *theme*, and a merchant who has written Hyper's
   * footer promises and then switched to Voltic must keep them - switching
   * back is one click, and losing an afternoon's writing to it would be
   * unforgivable. So keys this build cannot resolve are carried through a save
   * rather than dropped, and the editor simply does not offer them.
   *
   * Defaulted, so every theme saved before merchants could edit their copy
   * parses to a shop that renders its theme's own words - which is what it was
   * rendering.
   */
  content: z.record(z.string(), z.string()).default({}),
  /**
   * The language switcher. Optional, so every theme saved before languages
   * existed - and every hand-built theme object - is still a theme; read it
   * through `themeLanguageSwitcherSchema.parse(theme.language_switcher ?? {})`
   * to get the defaults.
   */
  language_switcher: themeLanguageSwitcherSchema.optional(),
  /**
   * Shop-wide add-ons. Optional for the reason `language_switcher` is; read it
   * through `themePlugin()` in `plugins/registry.ts` to get the defaults.
   */
  plugins: themePluginsSchema.optional(),
  /**
   * Product card behaviour: hover image, badges, swatches, quick add.
   * Optional for the reason `plugins` is; read it through `themeCards()`.
   */
  cards: themeCardsSchema.optional(),
  /**
   * A built theme's design constants over its family's. Optional and
   * nullable for the reason `plugins` is: absent or null is the family's own
   * tokens, which is every theme that was not built from screenshots.
   */
  tokens: themeTokensOverrideSchema.nullish().catch(null),
});

/**
 * The merchant pages a theme may design, by handle - see `content_pages`.
 * Mirrors `CONTENT_PAGE_HANDLES` in `app/schemas/store.py`.
 */
export const CONTENT_PAGE_HANDLES = ['about', 'team', 'book', 'services'] as const;
export type ContentPageHandle = (typeof CONTENT_PAGE_HANDLES)[number];

/** Whether `handle` is one a theme may design a page for. */
export function isContentPageHandle(handle: string): handle is ContentPageHandle {
  return (CONTENT_PAGE_HANDLES as readonly string[]).includes(handle);
}

export type Theme = z.infer<typeof themeSchema>;
export type ThemeColors = z.infer<typeof themeColorsSchema>;
export type ThemeTypography = z.infer<typeof themeTypographySchema>;
export type ThemeHeader = z.infer<typeof themeHeaderSchema>;
export type ThemeFooter = z.infer<typeof themeFooterSchema>;
export type HeaderBanner = z.infer<typeof headerBannerSchema>;
export type ThemeLayout = z.infer<typeof themeLayoutSchema>;
/** A theme's id. See `layoutFamilySchema` for why this is not a union. */
export type LayoutFamily = string;
export type LayoutArea = 'header' | 'footer' | 'product' | 'listing';

/** The storefront's name for the same document. */
export type StorefrontTheme = Theme;
